import { z } from "zod";
import {
  OPPORTUNITY_STATUS_VALUES,
  type OpportunityStatus,
} from "../schemas/opportunities";
import {
  ERPSyncConfigSchema,
  OpportunityCustomFieldMappingSchema,
  OpportunityFieldMappingSchema,
} from "./schemas";

const statusMappingShape = OPPORTUNITY_STATUS_VALUES.reduce(
  (acc, status) => ({ ...acc, [status]: z.string().min(1) }),
  {} as Record<OpportunityStatus, z.ZodString>,
);

/**
 * Per-customer Salesforce sync configuration. Stored as `integrationConfig`
 * jsonb on the `erpIntegrationSyncsTable` row. Extends the shared
 * {@link ERPSyncConfigSchema} with the Salesforce-specific opportunity status
 * to stage mapping. Unlike HubSpot there is no pipeline selection — Salesforce
 * uses a flat stage list. Each FP status must map to a distinct Salesforce
 * stage so pushed opportunities don't collide on the same stage.
 */
export const SalesforceSyncConfigSchema = ERPSyncConfigSchema.extend({
  statusMapping: z
    .object(statusMappingShape)
    .optional()
    .meta({
      title: "Opportunity status mapping (legacy)",
      description:
        "Legacy category-based mapping. Kept for backwards compatibility — new configs use stageMapping instead.",
    }),
  stageMapping: z
    .record(z.string(), z.string().min(1))
    .optional()
    .meta({
      title: "Opportunity stage mapping",
      description:
        "Map custom FP opportunity stages to Salesforce opportunity stages. When set, takes priority over status mapping.",
    }),
  defaultCloseDateOffsetDays: z
    .number()
    .int()
    .min(1)
    .max(365)
    .default(90)
    .meta({
      title: "Default Close Date offset (days)",
      description:
        "Days from today used as the default Close Date when one isn't set.",
    }),
  opportunityFieldMapping: OpportunityFieldMappingSchema.meta({
    title: "Opportunity field mapping",
    description:
      "Map Forgepoint opportunity fields to Salesforce opportunity fields",
  }),
  opportunityCustomFieldMapping: OpportunityCustomFieldMappingSchema.meta({
    title: "Opportunity custom field mapping",
    description:
      "Map Forgepoint opportunity custom fields to Salesforce opportunity fields",
  }),
});

/**
 * Per-customer Salesforce sync configuration type. Inferred from
 * `SalesforceSyncConfigSchema`.
 */
export type SalesforceSyncConfig = z.infer<typeof SalesforceSyncConfigSchema>;
